/* eslint-disable react/no-multi-comp */
import { useEffect, useMemo, useRef, useState } from "react";

import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import rehypeReact, { Components } from "rehype-react";
import production from "react/jsx-runtime";

import CopyButton from "../CopyButton";

export function Code({ children }: React.HTMLProps<HTMLElement>) {
  return <code className="rounded-md bg-purple-100 px-1 py-0.5 font-mono text-xs">{children}</code>;
}

function CodeBlock(props: React.HTMLProps<HTMLElement>) {
  return (
    <figure
      {...props}
      className="rounded border border-gray [&_code]:bg-transparent [&_code]:p-0 [&_pre]:overflow-x-auto [&_pre]:px-2 [&_pre]:py-1.5"
    />
  );
}

function CodeBlockHeader({ children, ...props }: React.HTMLProps<HTMLElement>) {
  const ref = useRef<HTMLDivElement>(null);

  const [content, setContent] = useState("");

  useEffect(() => {
    setContent(ref.current?.nextElementSibling?.textContent || "");
  }, [ref]);

  return (
    <figcaption
      {...props}
      ref={ref}
      className="flex h-8 items-center gap-1 rounded-t-md border-b border-gray bg-slate-100 px-2"
    >
      <span className="text-xs">{children}</span>

      <CopyButton iconOnly={true} contentToCopy={content} className="-mr-1 ml-auto" />
    </figcaption>
  );
}

function Anchor(props: React.HTMLProps<HTMLAnchorElement>) {
  return (
    <a
      {...props}
      className="text-inherit underline underline-offset-[1.5px]"
      target="_blank"
      rel="noreferrer noopener"
    />
  );
}

export function Markdown({
  children,
  components,
}: {
  children: string | undefined;
  components?: Partial<Components>;
}) {
  const markdown = useMemo(() => {
    return unified()
      .use(remarkParse)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeRaw)
      .use(rehypeSanitize, {
        tagNames: [...(defaultSchema.tagNames ?? []), "figure", "figcaption"],
      })
      .use(rehypeReact, {
        ...production,
        components: {
          a: Anchor, // eslint-disable-line id-length
          code: Code,
          figure: CodeBlock,
          figcaption: CodeBlockHeader,
          ...components,
        },
      })
      .processSync(children).result;
  }, [children, components]);

  return markdown;
}
